import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useCampaignsQuery } from "hooks/usePromotions";
import { API } from 'utils/Helper';
import { formatUserDate, formatUserDateTime } from 'utils/timezoneHelper';

// Glassmorphic Premium Campaign Container
const CampaignGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  margin: 16px 0;
`;

const CampaignCard = styled.div`
  background: rgba(30, 41, 59, 0.4);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(168, 85, 247, 0.4);
    box-shadow: 0 10px 25px -5px rgba(168, 85, 247, 0.15),
                0 8px 10px -6px rgba(168, 85, 247, 0.15);
  }
`;

const BannerWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 120px;
  background: #0f172a;
  overflow: hidden;
`;

const BannerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;

  ${CampaignCard}:hover & {
    transform: scale(1.05);
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  bottom: -16px;
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.8);
  border: 1.5px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
`;

const IconImg = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const CardContent = styled.div`
  padding: 20px 12px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CardTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #f8fafc;
  margin: 0;
  line-height: 1.3;
`;

const CardDesc = styled.p`
  font-size: 11px;
  color: #94a3b8;
  margin: 0;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const CardFooter = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding-top: 12px;
`;

const StatusBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #a855f7;
  letter-spacing: 0.05em;
`;

const EndTimeText = styled.span`
  font-size: 10px;
  color: #64748b;
`;

const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const SkeletonCardWrapper = styled.div`
  .shimmer-banner {
    width: 100%;
    height: 140px;
    background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
    background-size: 600px 100%;
    animation: ${shimmer} 1.4s infinite linear;
    border-radius: 10px 10px 0 0;
  }

  .shimmer-footer {
    min-height: 55px;
    background: #292d2e;
    border: 1px solid #3a4142;
    border-top: none;
    border-radius: 0 0 10px 10px;
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .shimmer-title {
    width: 120px;
    height: 14px;
    background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
    background-size: 600px 100%;
    animation: ${shimmer} 1.4s infinite linear;
    border-radius: 4px;
  }

  .shimmer-progress {
    width: 70px;
    height: 14px;
    background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
    background-size: 600px 100%;
    animation: ${shimmer} 1.4s infinite linear;
    border-radius: 4px;
  }
`;

export default function CampaignsList({ showOn }) {
  const { data: campaigns = [], isLoading: loading } = useCampaignsQuery(showOn);
  const [selectedPromo, setSelectedPromo] = useState(null);

  if (loading) {
    if (showOn === "promotions") {
      return (
        <div className="main-card-promot" style={{ marginTop: "20px" }}>
          {[1, 2, 3].map((i) => (
            <SkeletonCardWrapper key={i} className="CardPromotion-main-in">
              <div className="shimmer-banner" />
              <div className="shimmer-footer">
                <div className="shimmer-title" />
                <div className="shimmer-progress" />
              </div>
            </SkeletonCardWrapper>
          ))}
        </div>
      );
    }

    return (
      <div className="my-6">
        <CampaignGrid>
          {[1, 2, 3].map((i) => (
            <SkeletonCardWrapper key={i}>
              <div className="shimmer-banner" style={{ height: "120px" }} />
              <div className="shimmer-footer">
                <div className="shimmer-title" />
                <div className="shimmer-progress" />
              </div>
            </SkeletonCardWrapper>
          ))}
        </CampaignGrid>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return null; // Don't render anything if no active campaigns for this slot
  }

  return (
    <>
      {showOn === "promotions" ? (
        <div className="main-card-promot" style={{ marginTop: "20px" }}>
          {campaigns.map((camp) => {
            const display = camp.display_json || {};
            const rawBanner = display.banner;
            const bannerUrl = rawBanner
              ? (rawBanner.startsWith("http") || rawBanner.startsWith("/uploads")
                  ? (rawBanner.startsWith("http") ? rawBanner : `${API}${rawBanner}`)
                  : rawBanner)
              : "https://images.unsplash.com/photo-1518156677180-95a2893f3e9f?w=600";

            const rawIcon = display.icon;
            const iconUrl = rawIcon
              ? (rawIcon.startsWith("http") || rawIcon.startsWith("/uploads")
                  ? (rawIcon.startsWith("http") ? rawIcon : `${API}${rawIcon}`)
                  : rawIcon)
              : "/assets/bcimages/promotion/starimg.webp";

            return (
              <div 
                className="CardPromotion-main-in" 
                key={camp.id}
                onClick={() => setSelectedPromo(camp)}
                style={{ 
                  cursor: "pointer", 
                  transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)" 
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
                onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
              >
                <div className="bc-promotion-main">
                  <img 
                    src={bannerUrl} 
                    className="bcpromotionimg" 
                    alt={display.title || camp.name} 
                    style={{ 
                      borderRadius: "10px 10px 0 0",
                      display: "block",
                      objectFit: "cover",
                      width: "100%",
                      height: "140px"
                    }} 
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "https://images.unsplash.com/photo-1518156677180-95a2893f3e9f?w=600";
                    }}
                  />
                  <img 
                    src={iconUrl} 
                    className="rank-img" 
                    alt="Badge" 
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/assets/bcimages/promotion/starimg.webp";
                    }}
                  />
                </div>
                <div className="casino-button-in" style={{ minHeight: "65px", padding: "10px 12px", alignItems: "flex-start", gap: "10px" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px", textAlign: "left", overflow: "hidden", flex: 1 }}>
                    <span className="deposite-heading" style={{ color: "#fff", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                      {display.title || camp.name}
                    </span>
                    {display.description && (
                      <span style={{ 
                        color: "#94a3b8", 
                        fontSize: "11px", 
                        lineHeight: "1.35",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        whiteSpace: "normal"
                      }}>
                        {display.description}
                      </span>
                    )}
                    {camp.end_date && (
                      <span className="casino-button-in-time" style={{ fontSize: "11px", marginTop: "3px" }}>
                        Ends {formatUserDateTime(camp.end_date)}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", flexShrink: 0, alignSelf: "center" }}>
                    <span className="casino-progress" style={{ fontSize: "11px", fontWeight: "bold", whiteSpace: "nowrap" }}>In Progress</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="my-6">
          <CampaignGrid>
            {campaigns.map((camp) => {
              const display = camp.display_json || {};
              const rawBanner = display.banner;
              const bannerUrl = rawBanner
                ? (rawBanner.startsWith("http") || rawBanner.startsWith("/uploads")
                    ? (rawBanner.startsWith("http") ? rawBanner : `${API}${rawBanner}`)
                    : rawBanner)
                : "https://images.unsplash.com/photo-1518156677180-95a2893f3e9f?w=600";

              const rawIcon = display.icon;
              const iconUrl = rawIcon
                ? (rawIcon.startsWith("http") || rawIcon.startsWith("/uploads")
                    ? (rawIcon.startsWith("http") ? rawIcon : `${API}${rawIcon}`)
                    : rawIcon)
                : "/assets/bcimages/promotion/starimg.webp";

              return (
                <CampaignCard key={camp.id} onClick={() => setSelectedPromo(camp)}>
                  <BannerWrapper>
                    <BannerImg 
                      src={bannerUrl} 
                      alt={display.title} 
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "https://images.unsplash.com/photo-1518156677180-95a2893f3e9f?w=600";
                      }}
                    />
                    <IconWrapper>
                      <IconImg 
                        src={iconUrl} 
                        alt="Campaign Icon" 
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = "/assets/bcimages/promotion/starimg.webp";
                        }}
                      />
                    </IconWrapper>
                  </BannerWrapper>
                  <CardContent>
                    <CardTitle>{display.title || camp.name}</CardTitle>
                    <CardDesc title={display.description}>{display.description || "Grab your bonus now!"}</CardDesc>
                    <CardFooter>
                      <StatusBadge>Active</StatusBadge>
                      {camp.end_date && (
                        <EndTimeText>
                          Ends {formatUserDate(camp.end_date)}
                        </EndTimeText>
                      )}
                    </CardFooter>
                  </CardContent>
                </CampaignCard>
              );
            })}
          </CampaignGrid>
        </div>
      )}

      {/* Promotion Detail Modal */}
      {selectedPromo && (
        <div 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px"
          }}
          onClick={() => setSelectedPromo(null)}
        >
          <div 
            style={{
              backgroundColor: "#1a2c38",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "420px",
              padding: "12px 12px 14px 12px",
              boxShadow: "0 25px 40px -10px rgba(0, 0, 0, 0.6)",
              color: "#fff",
              position: "relative"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Banner Image Container with Padding & Rounded Corners */}
            <div style={{ 
              width: "100%", 
              aspectRatio: "16/9", 
              maxHeight: "185px", 
              overflow: "hidden", 
              backgroundColor: "#0f212e", 
              borderRadius: "10px",
              position: "relative"
            }}>
              {/* Close Button */}
              <button
                onClick={() => setSelectedPromo(null)}
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  backgroundColor: "rgba(0, 0, 0, 0.75)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "50%",
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  cursor: "pointer",
                  zIndex: 10,
                  fontSize: "13px"
                }}
              >
                ✕
              </button>
              <img 
                src={
                  selectedPromo.display_json?.banner
                    ? (selectedPromo.display_json.banner.startsWith("http") || selectedPromo.display_json.banner.startsWith("/uploads")
                        ? (selectedPromo.display_json.banner.startsWith("http") ? selectedPromo.display_json.banner : `${API}${selectedPromo.display_json.banner}`)
                        : selectedPromo.display_json.banner)
                    : "https://images.unsplash.com/photo-1518156677180-95a2893f3e9f?w=600"
                }
                alt={selectedPromo.display_json?.title || selectedPromo.name}
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "https://images.unsplash.com/photo-1518156677180-95a2893f3e9f?w=600";
                }}
              />
            </div>

            {/* Modal Body */}
            <div style={{ padding: "12px 4px 0 4px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: "bold", color: "#fff", letterSpacing: "-0.01em" }}>
                  {selectedPromo.display_json?.title || selectedPromo.name}
                </h3>
                <span style={{ 
                  backgroundColor: "rgba(0, 231, 1, 0.15)", 
                  color: "#00e701", 
                  fontSize: "10px", 
                  fontWeight: "bold", 
                  padding: "2px 8px", 
                  borderRadius: "12px",
                  border: "1px solid rgba(0, 231, 1, 0.3)",
                  textTransform: "uppercase"
                }}>
                  Active
                </span>
              </div>

              {selectedPromo.display_json?.description && (
                <p style={{ 
                  color: "#94a3b8", 
                  fontSize: "13px", 
                  lineHeight: "1.5", 
                  margin: "0 0 12px 0",
                  whiteSpace: "pre-line"
                }}>
                  {selectedPromo.display_json.description}
                </p>
              )}

              {selectedPromo.end_date && (
                <div style={{ 
                  backgroundColor: "#0f212e", 
                  padding: "8px 12px", 
                  borderRadius: "8px", 
                  fontSize: "11px",
                  color: "#94a3b8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                  <span>Promotion Period:</span>
                  <span style={{ color: "#fff", fontWeight: "600" }}>
                    Ends {formatUserDate(selectedPromo.end_date)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
